import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  CaseReducer,
} from '@reduxjs/toolkit';
import { DataRoles, ModifyRole, ParamsGetRole, Role } from '../models/role';

import roleService from '../services/role';
import { RequestLoading } from '@app/utils/constants';
import { handleErrorResponse } from '@app/utils/helpers';

const requests = [
  'getRoles',
  'createRole',
  'updateRole',
  'updateRoleStatus',
] as const;

type RoleRequest = RequestLoading<(typeof requests)[number]>;

interface State {
  dataRoles: DataRoles;
  systemRoleList: Role[];
  getRoleLoading: boolean;
  selectedRole: Role | undefined;
  roleRequest: RoleRequest;
}

const initialState: State = {
  dataRoles: {
    data: [],
    paging: undefined,
  },
  systemRoleList: [],
  getRoleLoading: false,
  selectedRole: undefined,
  roleRequest: {
    loading: false,
  },
};

type CR<T> = CaseReducer<State, PayloadAction<T>>;

const TYPE_PREFIX = 'admin/management-role/role/';

const getRoles = createAsyncThunk<DataRoles, ParamsGetRole>(
  TYPE_PREFIX + 'getUsers',
  async (params, { rejectWithValue }) => {
    try {
      const result = await roleService.getRoles(params);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const createNewRole = createAsyncThunk<Role, ModifyRole>(
  TYPE_PREFIX + 'createNewRole',
  async (data, { rejectWithValue }) => {
    try {
      const result = await roleService.createRole(data);

      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const updateRole = createAsyncThunk<Role, { id: number; data: ModifyRole }>(
  TYPE_PREFIX + 'updateRole',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const result = await roleService.updateRole(id, data);

      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const getSystemRoleList = createAsyncThunk<DataRoles, ParamsGetRole>(
  TYPE_PREFIX + 'getSystemRoleList',
  async (params, { rejectWithValue }) => {
    try {
      const result = await roleService.getRoles(params);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const getRoleDetail = createAsyncThunk<Role, number>(
  'admin/management-role/role/getRoleDetail',
  async (id, { rejectWithValue }) => {
    try {
      const result = await roleService.getRoleDetail(id);
      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

const updateRoleStatus = createAsyncThunk<
  Role,
  { id: number; isActive: boolean }
>(
  TYPE_PREFIX + 'updateRoleStatus',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const result = await roleService.updateRoleStatus(id, isActive);

      return result;
    } catch (error) {
      return rejectWithValue(handleErrorResponse(error));
    }
  },
);

// const updateUser = createAsyncThunk(
//   'admin/management-customer/customer/updateUser',
//   async (data: User) => {
//     const result = await userService.updateUser(data);
//     return result;
//   },
// );

// const getUserDetail = createAsyncThunk(
//   'admin/management-customer/customer/getUserDetail',
//   async (userId: string) => {
//     const result = await userService.getUserDetail(userId);
//     return result;
//   },
// );

// const searchTextUser = createAsyncThunk(
//   'admin/management-customer/customer/searchTextUser',
//   async (arg: { username: string, email: string, phoneNumber: string, fullName: string }) => {
//     const result = await userService.searchTextUser(
//       arg.email, arg.fullName, arg.phoneNumber, arg.username
//     );
//     return result;
//   },
// );
const slice = createSlice({
  name: 'admin/management-customer/customer',
  initialState,
  reducers: {
    setSelectedRole: (state, action) => {
      // state.selectedRole = action.payload;
    },
  },
  extraReducers: (builder) => {
    //searchTextUser
    builder.addCase(getRoles.pending, (state, action) => ({
      ...state,
      // getRoleLoading: true,
      roleRequest: {
        loading: true,
        name: 'getRoles',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getRoles.fulfilled, (state, action) => ({
      ...state,
      dataRoles: action.payload,
      // getRoleLoading: false,
      roleRequest: {
        loading: false,
        name: 'getRoles',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(getRoles.rejected, (state, action) => ({
      ...state,
      // getRoleLoading: true,
      roleRequest: {
        loading: false,
        name: 'getRoles',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    //getSystemRoleList
    builder.addCase(getSystemRoleList.pending, (state) => ({
      ...state,
      getRoleLoading: true,
    }));
    builder.addCase(getSystemRoleList.fulfilled, (state, { payload }) => ({
      ...state,
      systemRoleList: payload.data,
      getRoleLoading: false,
    }));
    builder.addCase(getSystemRoleList.rejected, (state) => ({
      ...state,
      getRoleLoading: true,
    }));

    //getRoleDetail
    builder.addCase(getRoleDetail.pending, (state) => ({
      ...state,
      getRoleLoading: true,
    }));
    builder.addCase(getRoleDetail.fulfilled, (state, { payload }) => ({
      ...state,
      selectedRole: payload,
      getRoleLoading: false,
    }));
    builder.addCase(getRoleDetail.rejected, (state) => ({
      ...state,
      getRoleLoading: true,
    }));

    // create new role
    builder.addCase(createNewRole.pending, (state, action) => ({
      ...state,
      roleRequest: {
        loading: true,
        name: 'createRole',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(createNewRole.fulfilled, (state, action) => ({
      ...state,
      roleRequest: {
        loading: false,
        name: 'createRole',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(createNewRole.rejected, (state, action) => ({
      ...state,
      roleRequest: {
        loading: false,
        name: 'createRole',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // update existed role
    builder.addCase(updateRole.pending, (state, action) => ({
      ...state,
      roleRequest: {
        loading: true,
        name: 'updateRole',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateRole.fulfilled, (state, action) => ({
      ...state,
      roleRequest: {
        loading: false,
        name: 'updateRole',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateRole.rejected, (state, action) => ({
      ...state,
      roleRequest: {
        loading: false,
        name: 'updateRole',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));

    // update existed role status
    builder.addCase(updateRoleStatus.pending, (state, action) => ({
      ...state,
      roleRequest: {
        loading: true,
        name: 'updateRoleStatus',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateRoleStatus.fulfilled, (state, action) => ({
      ...state,
      roleRequest: {
        loading: false,
        name: 'updateRoleStatus',
        status: action.meta.requestStatus,
      },
    }));
    builder.addCase(updateRoleStatus.rejected, (state, action) => ({
      ...state,
      roleRequest: {
        loading: false,
        name: 'updateRoleStatus',
        status: action.meta.requestStatus,
        error: action.payload ?? action.error,
      },
    }));
  },
});

export {
  getRoles,
  getSystemRoleList,
  getRoleDetail,
  createNewRole,
  updateRole,
  updateRoleStatus,
};
export const { setSelectedRole } = slice.actions;

export default slice.reducer;
